import {
  AppBar,
  Box,
  Button,
  Container,
  Paper,
  Toolbar,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import { Alert, AlertTitle } from "@material-ui/lab";
import { useAuth } from "auth";
import Colors from "Constants/Colors";
import LanguageCodes from "Constants/LanguageCodes";
import { Environment } from "Enums/Environment";
import i18n from "i18n";
import i18next from "i18next";
import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import { AngolianFlagIcon } from "Views/Components/Icons/AngolianFlagIcon";
import { BritishFlagIcon } from "Views/Components/Icons/BritishFlagIcon";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    alert: {
      marginBottom: theme.spacing(3),
      marginTop: theme.spacing(2),
      textAlign: "left",
    },
    contentContainer: {
      alignItems: "center",
      display: "flex",
      flexGrow: 1,
      justifyContent: "center",
      marginTop: theme.spacing(8),
      padding: theme.spacing(4, 2),
    },
    headerAcc: {
      backgroundColor: Colors.HEADER_ACC,
    },
    headerDev: {
      backgroundColor: Colors.HEADER_DEV,
    },
    iconWrapper: {
      display: "flex",
      justifyContent: "center",
      marginBottom: theme.spacing(2),
    },
    lockIcon: {
      color: theme.palette.warning.main,
      fontSize: 56,
    },
    logoutButton: {
      marginTop: theme.spacing(1),
    },
    paper: {
      borderRadius: 8,
      boxShadow: "0 4px 20px 0 rgba(0,0,0,0.12)",
      maxWidth: 560,
      padding: theme.spacing(4),
      textAlign: "center",
      width: "100%",
    },
    root: {
      backgroundColor: theme.palette.background.default,
      display: "flex",
      flexDirection: "column",
      minHeight: "100vh",
    },
    title: {
      flexGrow: 1,
    },
    userEmail: {
      color: theme.palette.text.secondary,
      marginBottom: theme.spacing(3),
    },
  }),
);

export const NoRolesAssigned: FC = () => {
  const classes = useStyles();
  const { auth } = useAuth();
  const { t } = useTranslation(["errors", "menu"]);

  const changeLanguage = (language: string) => {
    if (language !== i18next.language) {
      i18n.changeLanguage(language);
    }
  };

  const username =
    auth?.user?.email ||
    auth?.user?.username ||
    auth?.account?.username ||
    "";

  return (
    <div className={classes.root}>
      <AppBar
        position="fixed"
        className={
          process.env.REACT_APP_ENVIRONMENT_NAME === Environment.development
            ? classes.headerDev
            : process.env.REACT_APP_ENVIRONMENT_NAME === Environment.acceptance
            ? classes.headerAcc
            : undefined
        }
      >
        <Toolbar>
          <Typography variant="h6" className={classes.title}>
            {process.env.REACT_APP_ENVIRONMENT_NAME === Environment.development
              ? t("menu:title") + " - " + t("menu:environmentDev")
              : process.env.REACT_APP_ENVIRONMENT_NAME === Environment.acceptance
              ? t("menu:title") + " - " + t("menu:environmentAcc")
              : t("menu:title")}
          </Typography>
          <Tooltip title={t<string>("menu:englishButton")} arrow={true}>
            <Button
              color="inherit"
              onClick={() => changeLanguage(LanguageCodes.ENGLISH)}
            >
              <BritishFlagIcon />
            </Button>
          </Tooltip>
          <Tooltip title={t<string>("menu:portugueseButton")} arrow={true}>
            <Button
              color="inherit"
              onClick={() => changeLanguage(LanguageCodes.PORTUGUESE)}
            >
              <AngolianFlagIcon />
            </Button>
          </Tooltip>
          <Button color="inherit" onClick={() => auth?.logout()}>
            {t("menu:logout")}
          </Button>
        </Toolbar>
      </AppBar>
      <Container className={classes.contentContainer}>
        <Paper className={classes.paper} elevation={3}>
          <Box className={classes.iconWrapper}>
            <LockOutlinedIcon className={classes.lockIcon} />
          </Box>
          <Typography variant="h5" component="h1" gutterBottom={true}>
            {t("errors:noRolesTitle")}
          </Typography>
          <Alert severity="warning" className={classes.alert}>
            <AlertTitle>{t("errors:noRolesTitle")}</AlertTitle>
            {t("errors:noRolesMessage")}
          </Alert>
          {username && (
            <Typography variant="body2" className={classes.userEmail}>
              {username}
            </Typography>
          )}
          <Button
            variant="contained"
            color="primary"
            className={classes.logoutButton}
            onClick={() => auth?.logout()}
          >
            {t("menu:logout")}
          </Button>
        </Paper>
      </Container>
    </div>
  );
};
